import React, { useEffect, useMemo, useState } from "react";
import consoleApi from "../../services/consoleApiService";

/**
 * UsersView — admin breakdown of the users collection: total, runners vs run
 * clubs, signups over time (stacked monthly bars), and the most recent joins.
 * Styled to the console's light design tokens.
 */

const RUNNER_C = "var(--console-accent)";
const CLUB_C = "#10b981";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString() : "—");

function Stat({ label, value, sub, color }) {
  return (
    <div className="rc-uv-stat">
      <div className="rc-uv-stat-val" style={color ? { color } : undefined}>{value}</div>
      <div className="rc-uv-stat-label">{label}</div>
      {sub != null && <div className="rc-uv-stat-sub">{sub}</div>}
    </div>
  );
}

export default function UsersView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    consoleApi.stats
      .users({ recent_limit: 30, months: 12 })
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || e.message || "Failed to load user stats"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const monthly = data?.monthly || [];
  const maxMonth = useMemo(() => Math.max(1, ...monthly.map((m) => m.total || 0)), [monthly]);
  const total = data?.total || 0;
  const runners = data?.runners || 0;
  const runclubs = data?.runclubs || 0;
  const runnerPct = total ? Math.round((runners / total) * 100) : 0;
  const clubPct = total ? Math.round((runclubs / total) * 100) : 0;

  return (
    <div className="rc-uv">
      <style>{UV_CSS}</style>

      <div className="rc-uv-head">
        <div>
          <p className="rc-uv-eyebrow">Members</p>
          <h1 className="rc-uv-title">Users</h1>
          <p className="rc-uv-sub">Runners vs run clubs, growth over time, and the latest signups.</p>
        </div>
        <button type="button" className="rc-uv-ghost" onClick={load} disabled={loading}>
          {loading ? "Refreshing…" : "↻ Refresh"}
        </button>
      </div>

      {error ? (
        <div className="rc-uv-card rc-uv-error">{error}</div>
      ) : loading && !data ? (
        <div className="rc-uv-card rc-uv-muted">Loading user stats…</div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="rc-uv-stats">
            <Stat label="Total users" value={fmtNum(total)} />
            <Stat label="Runners" value={fmtNum(runners)} sub={`${runnerPct}%`} color={RUNNER_C} />
            <Stat label="Run clubs" value={fmtNum(runclubs)} sub={`${clubPct}%`} color={CLUB_C} />
            <Stat label="New · 7 days" value={fmtNum(data?.last_7_days)} />
            <Stat label="New · 30 days" value={fmtNum(data?.last_30_days)} />
          </div>

          {/* Split bar */}
          <div className="rc-uv-card">
            <div className="rc-uv-card-head"><h2>Runners vs run clubs</h2></div>
            <div className="rc-uv-split">
              {runners > 0 && (
                <div className="rc-uv-split-seg" style={{ width: `${runnerPct}%`, background: RUNNER_C }} title={`Runners: ${runners}`}>
                  {runnerPct >= 8 ? `${runnerPct}%` : ""}
                </div>
              )}
              {runclubs > 0 && (
                <div className="rc-uv-split-seg" style={{ width: `${clubPct}%`, background: CLUB_C }} title={`Run clubs: ${runclubs}`}>
                  {clubPct >= 8 ? `${clubPct}%` : ""}
                </div>
              )}
            </div>
            <div className="rc-uv-legend">
              <span><i style={{ background: RUNNER_C }} /> Runners · {fmtNum(runners)}</span>
              <span><i style={{ background: CLUB_C }} /> Run clubs · {fmtNum(runclubs)}</span>
              {data?.other > 0 && <span><i style={{ background: "var(--console-dim)" }} /> Other · {fmtNum(data.other)}</span>}
            </div>
          </div>

          {/* Monthly signups */}
          <div className="rc-uv-card">
            <div className="rc-uv-card-head">
              <h2>Signups by month</h2>
              <span className="rc-uv-muted">last 12 months</span>
            </div>
            <div className="rc-uv-chart">
              {monthly.map((m) => (
                <div key={m.key} className="rc-uv-bar-col" title={`${m.label}: ${m.total} (${m.runners} runners, ${m.runclubs} clubs)`}>
                  <div className="rc-uv-bar-stack" style={{ height: `${Math.round(((m.total || 0) / maxMonth) * 100)}%` }}>
                    {m.runclubs > 0 && <div className="rc-uv-bar-seg" style={{ flex: m.runclubs, background: CLUB_C }} />}
                    {m.runners > 0 && <div className="rc-uv-bar-seg" style={{ flex: m.runners, background: RUNNER_C }} />}
                  </div>
                  <div className="rc-uv-bar-count">{m.total || ""}</div>
                  <div className="rc-uv-bar-label">{m.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent signups */}
          <div className="rc-uv-card">
            <div className="rc-uv-card-head">
              <h2>Recent signups</h2>
              <span className="rc-uv-muted">{data?.recent?.length || 0} most recent</span>
            </div>
            {(!data?.recent || data.recent.length === 0) ? (
              <p className="rc-uv-muted">No signups with a recorded date.</p>
            ) : (
              <div className="rc-uv-table">
                <div className="rc-uv-tr rc-uv-th">
                  <span>Name</span><span>Type</span><span>Location</span><span>Joined</span>
                </div>
                {data.recent.map((u, i) => (
                  <div className="rc-uv-tr" key={`${u.email || u.name || i}-${i}`}>
                    <span className="rc-uv-name">
                      <strong>{u.name || "—"}</strong>
                      {u.email && <em>{u.email}</em>}
                    </span>
                    <span>
                      <span className={`rc-uv-pill ${u.user_type === "runclub" ? "club" : "runner"}`}>
                        {u.user_type === "runclub" ? "Run club" : u.user_type === "runner" ? "Runner" : (u.user_type || "—")}
                      </span>
                    </span>
                    <span className="rc-uv-loc">
                      {[u.city, u.state, u.country].filter(Boolean).join(", ") || "—"}
                    </span>
                    <span className="rc-uv-date">{fmtDate(u.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const UV_CSS = `
  .rc-uv { max-width:1100px; margin:0 auto; padding:4px 2px 60px; color:var(--console-ink); }
  .rc-uv-head { display:flex; align-items:flex-end; justify-content:space-between; gap:16px; margin-bottom:20px; flex-wrap:wrap; }
  .rc-uv-eyebrow { font-size:11px; letter-spacing:.26em; text-transform:uppercase; color:var(--console-accent); margin:0 0 6px; font-weight:700; }
  .rc-uv-title { font-size:26px; font-weight:800; letter-spacing:-.02em; margin:0; }
  .rc-uv-sub { color:var(--console-muted); margin:6px 0 0; font-size:14px; }
  .rc-uv-ghost { background:var(--console-surface); color:var(--console-ink); border:1px solid var(--console-border); border-radius:10px; padding:9px 14px; font-size:13px; font-weight:600; cursor:pointer; box-shadow:var(--console-shadow); }
  .rc-uv-ghost:hover { background:var(--console-s2); }
  .rc-uv-card { background:var(--console-surface); border:1px solid var(--console-border); border-radius:14px; padding:18px; box-shadow:var(--console-shadow); margin-bottom:16px; }
  .rc-uv-card-head { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:16px; }
  .rc-uv-card-head h2 { font-size:14px; font-weight:700; margin:0; }
  .rc-uv-muted { color:var(--console-muted); font-size:13px; }
  .rc-uv-error { color:var(--console-red); }

  .rc-uv-stats { display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:14px; margin-bottom:16px; }
  .rc-uv-stat { background:var(--console-surface); border:1px solid var(--console-border); border-radius:14px; padding:16px 18px; box-shadow:var(--console-shadow); }
  .rc-uv-stat-val { font-size:30px; font-weight:800; letter-spacing:-.02em; line-height:1; }
  .rc-uv-stat-label { font-size:12.5px; color:var(--console-muted); margin-top:8px; font-weight:600; }
  .rc-uv-stat-sub { font-size:12px; color:var(--console-dim); margin-top:2px; font-weight:700; }

  .rc-uv-split { display:flex; height:30px; border-radius:8px; overflow:hidden; background:var(--console-bg); border:1px solid var(--console-border); }
  .rc-uv-split-seg { display:flex; align-items:center; justify-content:center; color:#fff; font-size:12px; font-weight:800; min-width:2px; transition:width .3s; }
  .rc-uv-legend { display:flex; gap:18px; flex-wrap:wrap; margin-top:12px; font-size:13px; color:var(--console-ink); }
  .rc-uv-legend span { display:inline-flex; align-items:center; gap:7px; }
  .rc-uv-legend i { width:11px; height:11px; border-radius:3px; display:inline-block; }

  .rc-uv-chart { display:flex; align-items:flex-end; gap:10px; height:190px; padding-top:8px; }
  .rc-uv-bar-col { flex:1; display:flex; flex-direction:column; align-items:center; height:100%; justify-content:flex-end; min-width:0; }
  .rc-uv-bar-stack { width:70%; max-width:34px; min-height:2px; border-radius:5px 5px 0 0; overflow:hidden; display:flex; flex-direction:column; }
  .rc-uv-bar-seg { width:100%; }
  .rc-uv-bar-count { font-size:11px; font-weight:700; color:var(--console-ink); margin-top:5px; }
  .rc-uv-bar-label { font-size:10.5px; color:var(--console-muted); margin-top:1px; white-space:nowrap; }

  .rc-uv-table { display:flex; flex-direction:column; }
  .rc-uv-tr { display:grid; grid-template-columns:minmax(0,2.2fr) 1fr minmax(0,1.6fr) 1fr; gap:12px; align-items:center; padding:11px 6px; border-bottom:1px solid var(--console-line2); font-size:13.5px; }
  .rc-uv-tr:last-child { border-bottom:none; }
  .rc-uv-th { font-size:11px; text-transform:uppercase; letter-spacing:.08em; color:var(--console-dim); font-weight:700; border-bottom:1px solid var(--console-border); }
  .rc-uv-name { display:flex; flex-direction:column; min-width:0; }
  .rc-uv-name strong { font-weight:700; color:var(--console-ink); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .rc-uv-name em { font-style:normal; color:var(--console-muted); font-size:12px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .rc-uv-loc { color:var(--console-muted); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .rc-uv-date { color:var(--console-ink); white-space:nowrap; }
  .rc-uv-pill { display:inline-block; font-size:11px; font-weight:700; padding:3px 9px; border-radius:999px; }
  .rc-uv-pill.runner { background:var(--console-accent-bg); color:var(--console-accent); }
  .rc-uv-pill.club { background:rgba(16,185,129,.14); color:#0e9f6e; }
`;
